import { assert } from 'ts-essentials';
import { MutationType, STTLTimeNodePresetClassType } from '@tencent/slide-sdk-pb-model';
import { BaseMutation } from '../base/base-mutation';
import { SLIDE_PAGE_DATA_TYPES } from '../../data';
import { MEDIA_TRIGGER_ID_PREFIX } from '../../constants';
import { UnitTimeNode } from '../../model/unit-time-node';
import { checkSetAnimationData } from './animation-check-utils';
import type { MaybeError } from '../base/base-mutation';
import type { IEGTimeNodeChoice } from '@tencent/slide-sdk-pb-model';
import type { IValidSetAnimationPropertiesMutationData } from '../base/valid-mutation-data';
import type { PresentationDataModel } from '../../model/presentation-data-model';

export function getMediaParams(
    getTimeNode: (
        id: string,
        index: number,
    ) =>
        | (IEGTimeNodeChoice & {
              referenceCount?: number | undefined;
          })
        | undefined,
    shapeId: string,
    presetClass?: STTLTimeNodePresetClassType,
) {
    const timeNode: IEGTimeNodeChoice = {};
    if (presetClass === STTLTimeNodePresetClassType.mediacall) {
        const mediaTriggerId = `${MEDIA_TRIGGER_ID_PREFIX}${shapeId}`;
        const mediaTimeNode = getTimeNode(mediaTriggerId, 0);
        assert(mediaTimeNode, 'mediaTimeNode should not be undefined');
        if (mediaTimeNode?.video) timeNode.video = mediaTimeNode.video;
        if (mediaTimeNode?.audio) timeNode.audio = mediaTimeNode.audio;
    }
    return timeNode;
}

export class SetAnimationMutation extends BaseMutation<
    PresentationDataModel,
    IValidSetAnimationPropertiesMutationData
> {
    public readonly type: MutationType = MutationType.SetAnimationProperties;

    public apply(presentation: PresentationDataModel) {
        if (!this.data.pageId) return;
        const { pageId, timeNode, triggerId, index } = this.data;
        const page = presentation.store.models.get(pageId, ...SLIDE_PAGE_DATA_TYPES);
        page?.store.timing?.store.setAnimation(triggerId, index, timeNode as IEGTimeNodeChoice);
    }

    public invert(presentation: PresentationDataModel): [IValidSetAnimationPropertiesMutationData] {
        const page = presentation.store.models.get(this.data.pageId, ...SLIDE_PAGE_DATA_TYPES);
        assert(page?.timing, 'page should have timing');
        const timeNode = page?.timing?.getTimeNode(this.data.triggerId, this.data.index);
        assert(timeNode, 'timeNode should not be undefined');

        const unitTimeNode = new UnitTimeNode({ ...timeNode });
        const mediaParams = getMediaParams(
            page.timing.getTimeNode.bind(page.timing),
            this.data.shapeId,
            unitTimeNode.presetClass,
        );
        unitTimeNode.setData({ ...unitTimeNode.getData(), ...mediaParams });

        const { pageId, shapeId, triggerId, index, seqType } = this.data;
        return [
            {
                type: MutationType.SetAnimationProperties,
                pageId,
                shapeId,
                triggerId,
                seqType,
                index,
                timeNode: unitTimeNode.getData(),
            },
        ];
    }

    public check(presentation: PresentationDataModel): MaybeError {
        const { pageId, shapeId, triggerId, index, seqType } = this.data;
        return checkSetAnimationData(presentation, seqType, triggerId, shapeId, pageId, index);
    }
}
